/**
 * CandidateReportPanel — the candidate side panel of the Candidates tab: the
 * candidate's header, the report and format pickers with one Download, a line
 * about the stored copy and the STORED PDF as the preview (epic
 * `2026-10-06-search-candidates-reports`, sub-task 04).
 *
 * THE PICKER IS ONE RADIO GROUP AND ONE DOWNLOAD, ON ONE LINE (epic
 * `2026-10-22-candidate-reports-four-kinds`, sub-task 03 — variant "D" of the
 * preview artifact, after Jared's "it may need to all be radio buttons with a
 * submit", 2026-10-24). One `radiogroup` "Report" of native radios with two
 * captioned halves, ASSESSMENTS [Recruiter | Client] and REFERENCES
 * [Official n | Backchannel n], read from `REPORT_GROUPS`: one Tab stop for
 * the choice, the arrows move within it (and skip a kind Thrive does not
 * have, which is disabled with its reason). Right beside it, in the same
 * bordered box (Jared, 2026-10-27: "the white background should include the
 * button to join it"), ONE [⬇ Download PDF | ▾] (`ReportDownloadButton`,
 * shared with the Reports cell): the ▾ holds the format, picking one only
 * renames the button, and the pick is remembered in this browser (Jared and Jose, 2026-10-27, replacing
 * the FORMAT radios that sat at the far right). The row never wraps: as the
 * panel narrows the captions go, then the radios become one select
 * (`PickerLayout`). The selected report's full name opens the version line,
 * so the stored copy is always named. The report selected first follows the
 * Reports cell's rule (`defaultReportKind`): the first that can serve a file,
 * else the first. The What's new slide draws this picker by hand
 * (`FourReportsDrawing` in `dashboard/WhatsNewIllustrations.tsx`): a change
 * to its shape should update that drawing too.
 *
 * THE FRAME IS THE PEOPLE LISTS PERSON PANEL'S, REBUILT, NOT IMPORTED
 * (`features/person-panel/PersonPanel.tsx` is another bounded context): a
 * React `role="dialog"` + `aria-modal` drawer from the right over a
 * `bg-bg opacity-60` scrim, half the window (`lg:w-1/2 lg:max-w-4xl`) with a
 * wider state (`lg:w-5/6 lg:max-w-6xl`) behind a size toggle, ‹ › over the
 * table's current page in its current sort, ←/→ and j/k to move, Escape to
 * close, Tab trapped inside. The keys are heard at the document and the drawer
 * is focusable for the reason PersonPanel's note gives: a click on plain text
 * must not drop focus to `<body>`, where every key would go dead. Arrows are
 * left alone inside a text field (the pickers' native radios included) and
 * inside a `radiogroup` — the radios move their own selection with them.
 *
 * Focus RETURN belongs to the host: it owns the table, and the row the reader
 * comes back to is the one the panel showed last, not the one it opened on.
 *
 * WHAT IS PREVIEWED IS WHAT IS DOWNLOADED. The preview is the stored PDF in
 * an `<iframe>` on a signed URL presigned with `disposition=inline` — no
 * fourth renderer to drift from the three the worker writes. The web serves no
 * Content-Security-Policy today (`infra/web-stack.ts` sets none), so nothing
 * blocks the S3 origin in a frame; the "Open PDF" link beside it is the way
 * out for a browser that will not render PDFs inline.
 *
 * BEFORE THE INDEX ANSWERS the two surfaces differ on purpose: the row's menu
 * is offered (a click on a copy that is not there toasts, which beats a
 * button that never wakes), while the panel's downloads wait for the answer —
 * the panel is where the reader looks at the state, so it shows it first.
 *
 * "Matches Thrive" compares the index's `source_updated_at` (the Thrive stamp
 * the stored copy was built from) with the projection's `*_updated_at` on the
 * row (what Thrive says now, as of the last dashboard build). A candidate not
 * on the current page has no row, so the panel says nothing about matching
 * rather than guessing.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchCandidateRow } from "@nu-assist/contracts";
import { Avatar, Spinner } from "@nu-assist/ui";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Linkedin,
  Mail,
  Maximize2,
  Minimize2,
  RefreshCw,
  X,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import {
  type CandidateReportFormat,
  type CandidateReportKind,
  type CandidateReportsClient,
  REPORT_GROUPS,
  REPORT_KIND_LABEL,
  REPORT_KIND_MISSING_REASON,
  REPORT_KIND_SHORT_LABEL,
  availableKinds,
  defaultReportKind,
  rowRefereeCount,
  rowSourceUpdatedAt,
} from "../../../clients/candidateReports.client";
import { RetryButton } from "../../../components/RetryButton";
import { StagePill } from "../../../components/stages/StagePill";
import { formatDaysInStage } from "../../../lib/format/date";
import { thriveContactUrl } from "../../../lib/thriveLinks";
import { formatLocation } from "./CandidatesTable";
import {
  CHECKING_STORED_COPY_REASON,
  FORMAT_NOT_STORED_REASON,
  ReportDownloadButton,
  pickerCaptionClass,
  preferredReportFormat,
  readRememberedReportFormat,
  refereesLabel,
  rememberReportFormat,
} from "./reportPicker";
import {
  UPDATING_FROM_THRIVE,
  reportStateFor,
  useCandidateReports,
  useOpenPdf,
  useRefreshCandidateReport,
  useReportDownload,
  useReportPreviewUrl,
} from "./useCandidateReports";
import { usePickerLayout } from "./usePickerLayout";

export const NO_REPORTS_MESSAGE =
  "No reports for this candidate yet. Thrive has no recruiter or client assessment on this candidacy and no official or backchannel references for this search.";

export interface CandidatePanelPosition {
  /** 0-based index of the open candidate in the WHOLE filtered set. */
  index: number;
  /** The table's total, not the page's length. */
  total: number;
}

export interface CandidateReportPanelProps {
  searchId: string;
  searchName: string | null | undefined;
  client: CandidateReportsClient;
  /** The candidacy in `?candidate=`. */
  candidacyId: string;
  /** Its row on the current page; null when it is not there (or not yet). */
  row: SearchCandidateRow | null;
  /** Null when the candidate is not on the current page. */
  position: CandidatePanelPosition | null;
  /** The page's rows have not loaded yet (a deep link). */
  positionLoading?: boolean;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  /** The row's photo, resolved by the tab (`useResolveThriveAvatars`). */
  avatarUrl?: string;
  /** "Now" for days in stage; injectable for tests. */
  now?: Date;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const iconButton =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-text-secondary hover:bg-surface-raised hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

/** A text action (Refresh, Open PDF); unavailable is `aria-disabled`. */
const actionClass =
  "inline-flex items-center gap-1 text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent aria-disabled:cursor-not-allowed aria-disabled:opacity-60 aria-disabled:hover:no-underline";

const linkClass =
  "inline-flex items-center gap-1 text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function isArrowOwner(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable) {
    return true;
  }
  return Boolean(el.closest('[role="radiogroup"], [role="tablist"], [role="menu"]'));
}

/** `Oct 1, 2026`; null for a missing or unparseable stamp. */
function formatBuiltDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Do two ISO stamps name the same instant? Compared as instants, not strings:
 * the index and the projection are written by different processes, and
 * `+00:00` vs `Z` must not read as "Thrive changed since".
 */
export function sameInstant(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isNaN(ta) || Number.isNaN(tb) ? a === b : ta === tb;
}

/**
 * Has Thrive changed since the stored report was built? Only when the table's
 * stamp (the search's last refresh) is LATER than the report's. A report
 * rebuilt on a click reads Thrive after the table did (#874), so its stamp
 * can be the newer one — and then the stored copy is the most current thing
 * on the screen, not a stale one. Unparseable stamps fall back to inequality.
 */
export function thriveChangedSince(
  tableStamp: string | null | undefined,
  reportStamp: string | null | undefined
): boolean {
  if (!tableStamp || !reportStamp || sameInstant(tableStamp, reportStamp)) return false;
  const table = Date.parse(tableStamp);
  const report = Date.parse(reportStamp);
  return Number.isNaN(table) || Number.isNaN(report) ? true : table > report;
}

function titleAtCompany(row: SearchCandidateRow): string | null {
  if (row.title && row.company) return `${row.title} at ${row.company}`;
  return row.title || row.company || null;
}

function CandidateHeader({
  row,
  candidacyId,
  nameId,
  avatarUrl,
  now,
}: {
  row: SearchCandidateRow | null;
  candidacyId: string;
  nameId: string;
  avatarUrl?: string;
  now: Date;
}) {
  if (!row) {
    return (
      <div className="flex items-center gap-4 px-6 py-5">
        <Avatar size="xl" />
        <h2 id={nameId} data-testid="candidate-panel-name" className="text-h2 text-text">
          Candidacy {candidacyId}
        </h2>
      </div>
    );
  }
  const location = formatLocation(row.city, row.state);
  const role = titleAtCompany(row);
  const contactId = row.contact_id == null ? null : String(row.contact_id);
  return (
    <div className="flex items-start gap-4 px-6 py-5">
      <Avatar src={avatarUrl} name={row.name} size="xl" />
      <div className="flex min-w-0 flex-col gap-1.5">
        <h2 id={nameId} data-testid="candidate-panel-name" className="text-h2 text-text">
          {row.name ?? "Unnamed candidate"}
        </h2>
        {role ? (
          <p data-testid="candidate-panel-role" className="text-body text-text-secondary">
            {role}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-caption text-text-secondary">
          <StagePill stage={row.stage} />
          {row.stage ? (
            <span data-testid="candidate-panel-days">
              {formatDaysInStage(row.stage_since, now)}
            </span>
          ) : null}
          {location ? <span data-testid="candidate-panel-location">· {location}</span> : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {contactId ? (
            <a
              href={thriveContactUrl(contactId)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="candidate-panel-thrive"
              className={linkClass}
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              Thrive
            </a>
          ) : null}
          {row.linkedin_url ? (
            <a
              href={row.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="candidate-panel-linkedin"
              className={linkClass}
            >
              <Linkedin className="h-3.5 w-3.5" aria-hidden="true" />
              LinkedIn
            </a>
          ) : null}
          {row.email ? (
            <a
              href={`mailto:${row.email}`}
              data-testid="candidate-panel-email"
              className={linkClass}
            >
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              {row.email}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The panel's body: pickers, Download, stored line, preview, empty states. */
function ReportsBody({
  searchId,
  candidacyId,
  client,
  row,
}: {
  searchId: string;
  candidacyId: string;
  client: CandidateReportsClient;
  row: SearchCandidateRow | null;
}) {
  const { query, phase } = useCandidateReports({
    client,
    searchId,
    candidacyId,
  });
  const kinds = availableKinds(row, query.data?.reports);
  const [chosen, setChosen] = useState<CandidateReportKind | null>(null);
  const kind =
    chosen && kinds.includes(chosen)
      ? chosen
      : defaultReportKind(kinds, (k) => reportStateFor(query.data, k));
  const state = kind ? reportStateFor(query.data, kind) : undefined;
  // The panel waits for the index: before it answers, no format is offered.
  const storedFormats = state?.formats ?? [];
  const [chosenFormat, setChosenFormat] = useState<CandidateReportFormat | null>(null);
  // The reader's last pick in this browser is the PREFERRED default (Jose,
  // 2026-10-27), not a pick: it goes through the pinning below like any
  // default, so a poll that later stores it does not switch the button
  // under the reader (/pr-review #1102 iter 1).
  const [remembered] = useState(readRememberedReportFormat);
  const pickFormat = (next: CandidateReportFormat) => {
    setChosenFormat(next);
    rememberReportFormat(next);
  };
  // Which one-line shape fits the row (`usePickerLayout`).
  const picker = usePickerLayout();
  const layout = picker.layout;
  // A default, once the index has answered, is pinned (/pr-review #1092 N1):
  // a later poll that makes another kind servable, or adds a format, must not
  // switch the selection under a reader who never picked. It moves only when
  // the pinned value stops being offered. The format is pinned per kind, so
  // picking another kind still opens on that kind's own default.
  const [pinnedFormat, setPinnedFormat] = useState<{
    kind: CandidateReportKind;
    format: CandidateReportFormat;
  } | null>(null);
  const format =
    chosenFormat && storedFormats.includes(chosenFormat)
      ? chosenFormat
      : pinnedFormat?.kind === kind && storedFormats.includes(pinnedFormat.format)
        ? pinnedFormat.format
        : preferredReportFormat(storedFormats, remembered);
  const answered = query.data !== undefined;
  useEffect(() => {
    if (!answered) return;
    if (chosen === null && kind) setChosen(kind);
    if (chosenFormat === null && kind && format && pinnedFormat?.kind !== kind) {
      setPinnedFormat({ kind, format });
    }
  }, [answered, chosen, kind, chosenFormat, format, pinnedFormat]);
  const kindName = useId();
  const reportCaptionId = useId();
  const reasonIdBase = useId();
  const { busy, download } = useReportDownload(client, searchId, candidacyId);
  const refresh = useRefreshCandidateReport(client, searchId, candidacyId);
  // After a Refresh's 202 the button that had focus unmounts (the kind turns
  // `building`); focus goes to the status line it changed, inside the dialog.
  const statusRef = useRef<HTMLDivElement | null>(null);
  const [refreshedTimes, setRefreshedTimes] = useState(0);
  useEffect(() => {
    if (refreshedTimes > 0) statusRef.current?.focus();
  }, [refreshedTimes]);
  const startRefresh = (k: CandidateReportKind) => {
    if (refresh.isPending) return;
    refresh.mutate(k, { onSuccess: () => setRefreshedTimes((n) => n + 1) });
  };
  const openPdf = useOpenPdf(client, searchId, candidacyId);
  const preview = useReportPreviewUrl(client, searchId, candidacyId, kind, state);
  // The URL the frame LOADED, pinned per kind and stored version: should the
  // preview query refetch for any reason, swapping the frame's `src` to the
  // new link would reload the document under the reader.
  const previewKey = state ? `${state.kind}:${state.built_at}:${state.version_count}` : "";
  const [pinned, setPinned] = useState<{ key: string; url: string } | null>(null);
  const frameUrl =
    pinned?.key === previewKey ? pinned.url : preview.data ? preview.data.url : undefined;
  useEffect(() => {
    if (preview.data && pinned?.key !== previewKey) {
      setPinned({ key: previewKey, url: preview.data.url });
    }
  }, [preview.data, pinned, previewKey]);

  // With no row, the kinds come from the index alone: never say "no reports"
  // until a read has SUCCEEDED (/pr-review #1021 iter 1). A failed read is the
  // error and its Retry; one still in flight is loading.
  if (!row && !query.data) {
    return query.isError ? (
      <div
        data-testid="candidate-panel-error"
        className="mx-6 my-4 flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
      >
        <output>Couldn’t load this candidate’s reports.</output>
        <RetryButton onClick={() => void query.refetch()} busy={query.isFetching} />
      </div>
    ) : (
      <p data-testid="candidate-panel-loading" className="px-6 py-4 text-body text-text-secondary">
        Loading reports…
      </p>
    );
  }
  if (!kind) {
    return (
      <p
        data-testid="candidate-panel-no-reports"
        className="px-6 py-6 text-body text-text-secondary"
      >
        {NO_REPORTS_MESSAGE}
      </p>
    );
  }

  const present = state?.status === "present";
  const building = state?.status === "building";
  // What `…/download` serves: the stored files when `present`, the previous
  // build's while `building` (#1020 iter 2) — a Refresh never hides a valid
  // report. Availability is `formats`, whatever the status.
  const servesFiles = Boolean(state && state.formats.length > 0);
  const servesPdf = Boolean(state?.formats.includes("pdf"));
  const buildPhase = phase(kind);
  const word = REPORT_KIND_LABEL[kind].toLowerCase();
  const thriveStamp = row ? rowSourceUpdatedAt(row, kind) : null;
  const built = formatBuiltDate(state?.built_at);
  // Why nothing can be downloaded, while that is so (the Download title and
  // the FORMAT radios' description). Not "no stored copy" before the index
  // has answered: it has not said so yet (/pr-review #1092 iter 1).
  const unstoredReason = !query.data
    ? query.isError
      ? "Couldn’t load this report’s state."
      : CHECKING_STORED_COPY_REASON
    : building
      ? "The report is being stored from Thrive."
      : "No stored copy of this report yet.";
  // With some formats stored, a missing one gets the Reports cell's sentence.
  const formatReason = storedFormats.length > 0 ? FORMAT_NOT_STORED_REASON : unstoredReason;
  // While any download of this candidate runs, Download waits (one at a time).
  const downloading = busy !== null;
  const canDownload = format !== null && !downloading;

  return (
    <div className="flex flex-1 flex-col gap-3 px-6 pb-6">
      <div
        ref={picker.ref}
        data-testid="candidate-panel-picker"
        data-layout={layout}
        className="flex flex-nowrap items-center border-y border-border py-3"
      >
        <span id={reportCaptionId} className="sr-only">
          Report
        </span>
        {/* One box around the report choice AND Download, so they read as one
            control (Jared, 2026-10-27: "the white background should include
            the button to join it"). Download sits beside the radiogroup, not
            inside it: a button is not one of the "Report" options. */}
        <div
          data-testid="candidate-panel-picker-box"
          className={[
            "inline-flex flex-nowrap items-center rounded-md border border-border bg-surface p-1",
            // Only the select may shrink; the radios keep their width and the
            // row's overflow check steps the shape down instead.
            layout === "narrow" ? "min-w-0" : null,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {layout === "narrow" ? (
            <select
              aria-labelledby={reportCaptionId}
              data-testid="candidate-panel-kind-select"
              value={kind}
              onChange={(e) => setChosen(e.target.value as CandidateReportKind)}
              className="min-w-0 max-w-full shrink rounded-sm bg-surface px-2 py-1.5 text-label font-semibold text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {REPORT_GROUPS.map((group) => (
                <optgroup key={group.id} label={group.label}>
                  {group.kinds.map((k) => {
                    const referees = row ? rowRefereeCount(row, k) : null;
                    const count = referees !== null && referees > 0 ? ` · ${referees}` : "";
                    return (
                      <option
                        key={k}
                        value={k}
                        disabled={!kinds.includes(k)}
                        // Why a report cannot be picked, on hover (the radios'
                        // sentence; a disabled option has no other place for it).
                        title={kinds.includes(k) ? undefined : REPORT_KIND_MISSING_REASON[k]}
                      >
                        {REPORT_KIND_SHORT_LABEL[k]}
                        {count}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          ) : (
            <div
              role="radiogroup"
              aria-labelledby={reportCaptionId}
              data-testid="candidate-panel-kinds"
              className="inline-flex flex-nowrap"
            >
              {REPORT_GROUPS.map((group, index) => (
                <div
                  key={group.id}
                  data-testid={`candidate-panel-group-${group.id}`}
                  className={`flex flex-col ${
                    index > 0
                      ? layout === "wide"
                        ? "ml-3.5 border-l border-border pl-3.5"
                        : "ml-2"
                      : layout === "wide"
                        ? "pr-1"
                        : ""
                  }`}
                >
                  {layout === "wide" ? (
                    <span aria-hidden="true" className={`${pickerCaptionClass} px-2 pt-1`}>
                      {group.label}
                    </span>
                  ) : null}
                  <div className="flex flex-nowrap">
                    {group.kinds.map((k) => {
                      const offered = kinds.includes(k);
                      // A count only when there is one: an unavailable kind reads
                      // "Official" next to its reason, never "Official 0" (canvas).
                      const referees = row ? rowRefereeCount(row, k) : null;
                      const count = referees !== null && referees > 0 ? referees : null;
                      const reason = offered ? undefined : REPORT_KIND_MISSING_REASON[k];
                      const reasonId = `${reasonIdBase}-${k}`;
                      return (
                        <label
                          key={k}
                          title={reason}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-sm px-2 pb-1.5 pt-1 text-label text-text hover:bg-surface-raised has-[:checked]:bg-accent-muted has-[:checked]:font-semibold has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-40 has-[:disabled]:hover:bg-transparent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent"
                        >
                          <input
                            type="radio"
                            name={kindName}
                            value={k}
                            checked={k === kind}
                            disabled={!offered}
                            onChange={() => setChosen(k)}
                            // The caption is a visual group; the name carries it.
                            aria-label={`${REPORT_KIND_LABEL[k]}${
                              count !== null ? `, ${refereesLabel(count)}` : ""
                            }`}
                            aria-describedby={reason ? reasonId : undefined}
                            data-testid={`candidate-panel-kind-${k}`}
                            className="m-0 cursor-pointer accent-accent disabled:cursor-not-allowed focus-visible:outline-none"
                          />
                          {REPORT_KIND_SHORT_LABEL[k]}
                          {count !== null ? (
                            <span
                              data-testid={`candidate-panel-count-${k}`}
                              className="font-normal tabular-nums text-text-secondary"
                            >
                              {count}
                            </span>
                          ) : null}
                          {reason ? (
                            <span id={reasonId} className="sr-only">
                              {reason}
                            </span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div
            className={`flex shrink-0 items-center ${
              layout === "wide" ? "ml-3.5 self-stretch border-l border-border pl-3.5" : "ml-2"
            }`}
          >
            <ReportDownloadButton
              format={format}
              offered={storedFormats}
              reason={() => formatReason}
              onFormatChange={pickFormat}
              size={layout === "medium" ? "short" : "full"}
              canDownload={canDownload}
              downloading={downloading}
              busyText={busy?.waiting ? UPDATING_FROM_THRIVE : undefined}
              reportName={REPORT_KIND_LABEL[kind]}
              title={format === null ? unstoredReason : undefined}
              onDownload={() => {
                if (format) void download(kind, format);
              }}
              testId="candidate-panel-download"
              formatTestIdPrefix="candidate-panel-format"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h3 data-testid="candidate-panel-report-title" className="text-h3 text-text">
          {REPORT_KIND_LABEL[kind]}
        </h3>
        {servesFiles && state ? (
          <p
            data-testid="candidate-panel-stored"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-text-secondary"
          >
            <span aria-hidden="true">·</span>
            <span data-testid="candidate-panel-version">Version {state.version_count}</span>
            {built ? <span>· Built {built}</span> : null}
            {/* Not while building: `source_updated_at` may already name the
                edit being built, not the one these files show. */}
            {present && thriveStamp && state.source_updated_at ? (
              thriveChangedSince(thriveStamp, state.source_updated_at) ? (
                <span data-testid="candidate-panel-match" className="text-warning-ink">
                  · Thrive changed since
                </span>
              ) : (
                <span data-testid="candidate-panel-match" className="text-success-ink">
                  · Matches Thrive
                </span>
              )
            ) : null}
            {present ? <span aria-hidden="true">·</span> : null}
            {present ? (
              <button
                type="button"
                data-testid="candidate-panel-refresh"
                aria-disabled={refresh.isPending || undefined}
                onClick={() => startRefresh(kind)}
                className={actionClass}
              >
                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                Refresh from Thrive
              </button>
            ) : null}
          </p>
        ) : null}
      </div>

      {query.isError && !query.data ? (
        <div
          data-testid="candidate-panel-error"
          className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
        >
          <output>Couldn’t load this candidate’s reports.</output>
          <RetryButton onClick={() => void query.refetch()} busy={query.isFetching} />
        </div>
      ) : null}

      {/* Always rendered, so a screen reader hears it change (a live region
          inserted with its text is often not announced); focus lands here
          after a Refresh. */}
      <div
        data-testid="candidate-panel-status"
        ref={statusRef}
        tabIndex={-1}
        aria-live="polite"
        className="focus:outline-none"
      >
        {building ? (
          <p
            data-testid="candidate-panel-building"
            data-phase={buildPhase ?? undefined}
            className="flex flex-wrap items-center gap-2 text-body text-text-secondary"
          >
            {buildPhase === "stalled" ? (
              <>
                <span>Still preparing — try Refresh from Thrive.</span>
                <button
                  type="button"
                  data-testid="candidate-panel-refresh-stalled"
                  aria-disabled={refresh.isPending || undefined}
                  onClick={() => startRefresh(kind)}
                  className={actionClass}
                >
                  <RefreshCw className="h-3 w-3" aria-hidden="true" />
                  Refresh from Thrive
                </button>
              </>
            ) : (
              <>
                <Spinner size="sm" label={servesFiles ? "Updating" : "Preparing"} />
                {buildPhase === "slow"
                  ? // Past the fast polls but before the API would honour a
                    // Refresh (600 s): no prompt for a button that does nothing.
                    servesFiles
                    ? `Still updating this ${word} report — this can take a few minutes. The version here stays downloadable.`
                    : "Still preparing — this can take a few minutes."
                  : servesFiles
                    ? `Updating this ${word} report from Thrive. The version here stays downloadable until the new one lands.`
                    : `Preparing this ${word} report from Thrive. It usually takes a few seconds.`}
              </>
            )}
          </p>
        ) : null}
      </div>

      {state && (state.status === "none" || state.status === "deleted") ? (
        <p data-testid="candidate-panel-not-stored" className="text-body text-text-secondary">
          This {word} report is not stored yet. It is stored the next time this search refreshes
          from Thrive.
        </p>
      ) : null}

      {servesPdf ? (
        <div className="flex flex-1 flex-col gap-2">
          {/* A button that presigns PER CLICK, not a link to the frame's URL:
              that one is five minutes old at most when the frame loads and
              older every minute the reader spends in it. Shown whatever
              happened to the preview — it is the way out when the preview
              could not be loaded. `aria-disabled` while busy, so the focused
              button keeps focus. */}
          <button
            type="button"
            data-testid="candidate-panel-open-pdf"
            data-waiting={openPdf.waiting || undefined}
            aria-disabled={openPdf.busy || undefined}
            aria-busy={openPdf.waiting || undefined}
            onClick={() => {
              if (!openPdf.busy) void openPdf.open(kind);
            }}
            className={`${actionClass} self-end`}
          >
            {openPdf.waiting ? (
              <>
                <Spinner size="sm" label={UPDATING_FROM_THRIVE} />
                {UPDATING_FROM_THRIVE}
              </>
            ) : (
              <>
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                Open PDF
              </>
            )}
          </button>
          {preview.data ? (
            <iframe
              src={frameUrl}
              title={`${REPORT_KIND_LABEL[kind]} report preview`}
              data-testid="candidate-panel-preview"
              referrerPolicy="no-referrer"
              // Out of the Tab order: keys typed inside a cross-origin PDF
              // viewer never reach this document, so Tab or Escape there
              // would leave the drawer's trap. A click still enters it.
              tabIndex={-1}
              className="h-[70vh] w-full rounded-sm border border-border bg-surface"
            />
          ) : preview.isError ? (
            <p
              data-testid="candidate-panel-preview-error"
              className="text-body text-text-secondary"
            >
              The preview could not be loaded. Open PDF and Download above still work.
            </p>
          ) : (
            <p
              data-testid="candidate-panel-preview-loading"
              className="text-body text-text-secondary"
            >
              Loading the preview…
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function CandidateReportPanel({
  searchId,
  searchName,
  client,
  candidacyId,
  row,
  position,
  positionLoading = false,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onClose,
  avatarUrl,
  now = new Date(),
}: CandidateReportPanelProps) {
  const [wide, setWide] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const nameId = useId();
  const hintId = useId();

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // An arrow that had focus and is now disabled (Next onto the page's last
  // row) no longer holds it: hand it to the other arrow, else to the drawer.
  useEffect(() => {
    const active = document.activeElement;
    const lost =
      (active === nextRef.current && !canNext) || (active === prevRef.current && !canPrev);
    if (!lost) return;
    if (active === nextRef.current && canPrev) prevRef.current?.focus();
    else if (active === prevRef.current && canNext) nextRef.current?.focus();
    else drawerRef.current?.focus();
  }, [canPrev, canNext]);

  const latest = useRef({ canPrev, canNext, onPrev, onNext, onClose });
  latest.current = { canPrev, canNext, onPrev, onNext, onClose };

  useEffect(() => {
    function handleKey(e: globalThis.KeyboardEvent) {
      const { canPrev, canNext, onPrev, onNext, onClose } = latest.current;
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const nodes = Array.from(
          drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
        ).filter((n) => !n.hasAttribute("disabled") && n.getAttribute("tabindex") !== "-1");
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!first || !last) return;
        const active = document.activeElement;
        const outside = active === drawerRef.current || !drawerRef.current?.contains(active);
        if (e.shiftKey && (active === first || outside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || outside)) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isArrowOwner(e.target)) return;
      if ((e.key === "ArrowLeft" || e.key === "k") && canPrev) {
        e.preventDefault();
        onPrev();
      } else if ((e.key === "ArrowRight" || e.key === "j") && canNext) {
        e.preventDefault();
        onNext();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const offPage = !position && !positionLoading;
  const listTitle = `Candidates · ${searchName || `Search ${searchId}`}`;

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: a pointer-only
          dismiss surface; Escape is the keyboard path (the document listener). */}
      <div
        aria-hidden="true"
        data-testid="candidate-panel-scrim"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-bg opacity-60"
      />
      <div
        ref={drawerRef}
        // biome-ignore lint/a11y/useSemanticElements: the People Lists panel's reason — a native <dialog> opened with showModal() would put the download menus and the toasts under the top layer.
        role="dialog"
        aria-modal="true"
        aria-labelledby={nameId}
        // Off the page both arrows are disabled and cannot take focus, so the
        // hint explaining why is the DIALOG's description, read on open.
        aria-describedby={offPage ? hintId : undefined}
        data-testid="candidate-panel"
        data-candidacy-id={candidacyId}
        data-size={wide ? "wide" : "half"}
        tabIndex={-1}
        className={`fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-border bg-bg shadow-lg focus:outline-none ${
          wide ? "lg:w-5/6 lg:max-w-6xl" : "lg:w-1/2 lg:max-w-4xl"
        }`}
      >
        <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2">
          <div
            className="min-w-0 flex-1 truncate text-h3 text-text"
            data-testid="candidate-panel-context"
          >
            From <span className="font-normal text-text-secondary">“{listTitle}”</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous candidate"
              title="Previous (←)"
              ref={prevRef}
              data-testid="candidate-panel-prev"
              disabled={!canPrev}
              onClick={onPrev}
              className={iconButton}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <span
              className="min-w-16 text-center text-caption tabular-nums text-text-secondary"
              data-testid="candidate-panel-position"
            >
              {position
                ? `${position.index + 1} of ${position.total}`
                : positionLoading
                  ? "…"
                  : "—"}
            </span>
            <button
              type="button"
              aria-label="Next candidate"
              title="Next (→)"
              ref={nextRef}
              data-testid="candidate-panel-next"
              disabled={!canNext}
              onClick={onNext}
              className={iconButton}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            aria-label={wide ? "Narrow the panel" : "Widen the panel"}
            aria-pressed={wide}
            title={wide ? "Narrower" : "Wider"}
            data-testid="candidate-panel-size"
            onClick={() => setWide((w) => !w)}
            className={`${iconButton} hidden lg:inline-flex`}
          >
            {wide ? (
              <Minimize2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close panel"
            title="Close (Esc)"
            data-testid="candidate-panel-close"
            onClick={onClose}
            className={iconButton}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {offPage ? (
          <p
            id={hintId}
            data-testid="candidate-panel-off-page"
            className="border-b border-border bg-surface px-4 py-1 text-caption text-text-muted"
          >
            This candidate is not on the page of the table you are looking at, so ‹ › are off.
          </p>
        ) : null}
        <div className="flex flex-1 flex-col overflow-y-auto">
          <CandidateHeader
            row={row}
            candidacyId={candidacyId}
            nameId={nameId}
            avatarUrl={avatarUrl}
            now={now}
          />
          {/* Keyed on the candidacy, so ‹ › start the next candidate on its
              own first report rather than on the kind the last one showed. */}
          <ReportsBody
            key={candidacyId}
            searchId={searchId}
            candidacyId={candidacyId}
            client={client}
            row={row}
          />
        </div>
      </div>
    </>
  );
}
